import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TelegramUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  language_code?: string;
  is_premium?: boolean;
}

interface PriceConfig {
  base_price: number;
  min_amount: number;
  max_amount: number;
  discount_threshold?: number;
  discount_percent?: number;
  label?: string;
  description?: string;
}

interface PaymentMethod {
  code: string;
  name: string;
  description?: string;
  icon_url?: string;
  commission_percent: number;
  commission_fixed: number;
  commission_text?: string;
  min_amount: number;
  max_amount?: number;
}

interface FAQItem {
  q: string;
  a: string;
}

interface AppState {
  // Theme
  theme: 'dark' | 'light';
  setTheme: (theme: 'dark' | 'light') => void;
  toggleTheme: () => void;

  // Language
  language: 'ru' | 'en';
  setLanguage: (lang: 'ru' | 'en') => void;

  // User
  user: TelegramUser | null;
  setUser: (user: TelegramUser | null) => void;

  // Site config from API
  settings: Record<string, string>;
  setSettings: (settings: Record<string, string>) => void;
  prices: Record<string, PriceConfig>;
  setPrices: (prices: Record<string, PriceConfig>) => void;
  paymentMethods: PaymentMethod[];
  setPaymentMethods: (methods: PaymentMethod[]) => void;
  faqs: FAQItem[];
  setFaqs: (faqs: FAQItem[]) => void;

  // USDT order flow
  usdtAmount: number;
  setUsdtAmount: (amount: number) => void;

  walletAddress: string;
  setWalletAddress: (address: string) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Theme
      theme: 'dark',
      setTheme: (theme) => {
        document.body.classList.toggle('light-theme', theme === 'light');
        set({ theme });
      },
      toggleTheme: () =>
        set((state) => {
          const newTheme = state.theme === 'dark' ? 'light' : 'dark';
          document.body.classList.toggle('light-theme', newTheme === 'light');
          return { theme: newTheme };
        }),

      // Language
      language: 'ru',
      setLanguage: (language) => set({ language }),

      // User
      user: null,
      setUser: (user) => set({ user }),

      // Site config
      settings: {},
      setSettings: (settings) => set({ settings }),
      prices: {},
      setPrices: (prices) => set({ prices }),
      paymentMethods: [],
      setPaymentMethods: (paymentMethods) => set({ paymentMethods }),
      faqs: [],
      setFaqs: (faqs) => set({ faqs }),

      // USDT order flow
      usdtAmount: 10,
      setUsdtAmount: (usdtAmount) => set({ usdtAmount }),

      walletAddress: '',
      setWalletAddress: (walletAddress) => set({ walletAddress }),
    }),
    {
      name: 'exchange-storage',
      partialize: (state) => ({
        theme: state.theme,
        language: state.language,
      }),
    }
  )
);
